'use client';
import {
  Button,
  ButtonColor,
  DateFormat,
  formatDate,
  formatName,
  formatTime,
  IconArrowRound,
  IconCalendar,
  IconClock,
  IconVideo,
  pluralize,
  ProfileImage,
} from '@leland-dev/leland-ui-library';
import { useState } from 'react';

import phog from '../../utils/analytics/posthog/posthog';

import { useBootcampCohortSelector } from './BootcampCohortSelector';
import { BootcampUrgencyTag } from './BootcampUrgencyTag';
import {
  type BootcampEnrollCohortItemBootcampCurriculumFragment,
  type BootcampEnrollCohortItemCohortFragment,
} from './__generated-gql-types__/BootcampEnrollCohortItem.generated';
import {
  formatBootcampCohortDate,
  openBootcampCohortCheckoutPage,
} from './utils';

interface BootcampEnrollCohortItemProps {
  cohort: BootcampEnrollCohortItemCohortFragment;
  maximumAttendees: Possible<number>;
}

export const BootcampEnrollCohortItem = ({
  cohort,
  maximumAttendees,
}: BootcampEnrollCohortItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const curriculums = cohort.sessions.reduce<
    BootcampEnrollCohortItemBootcampCurriculumFragment[]
  >((prev, curr) => {
    if (curr.content?.__typename === 'BootcampSession') {
      return [...prev, curr.content.curriculum];
    }
    return prev;
  }, []);

  const hasSameDuration =
    !!curriculums[0].duration &&
    curriculums.every(
      (curriculum) => curriculum.duration === curriculums[0].duration,
    );
  const [, setSelectedCohort] = useBootcampCohortSelector();

  const getOptimalCadences = (
    cohort: BootcampEnrollCohortItemCohortFragment,
  ): string[] => {
    const sessions = cohort.sessions;
    if (!sessions?.length) return [];

    const cadenceMap = new Map<string, number>();

    // Group sessions by day and time
    sessions.forEach((session) => {
      const date = new Date(session.startAt);
      const day = formatDate(date, DateFormat.LONG_DAY_OF_WEEK);
      const time = formatTime(date, { excludeTimeZone: true });
      const key = `${day}s at ${time}`;
      cadenceMap.set(key, (cadenceMap.get(key) ?? 0) + 1);
    });

    // Find patterns that occur more than once
    const optimalCadences = Array.from(cadenceMap.entries())
      .filter(([, count]) => count >= 2)
      .map(([pattern]) => pattern);

    return optimalCadences;
  };

  const cadences = getOptimalCadences(cohort);

  const seatsLeft = maximumAttendees
    ? maximumAttendees - cohort.totalAttendees
    : null;

  const onEnroll = () => {
    phog.capture('bootcamp_enroll_cohort_item--enroll-button-click', {
      cohort_id: cohort.id,
    });
    openBootcampCohortCheckoutPage(cohort.id);
  };

  const onViewCurriculum = () => {
    setSelectedCohort(cohort.id);
    const element = document.getElementById('bootcamp_page--curriculum_header');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const numberOfSessions = cohort.sessions.length;

  return (
    <div className="flex justify-between border-b py-4" key={cohort.id}>
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <div className="text-lg font-medium">
            {formatBootcampCohortDate(cohort)}
          </div>
        </div>
        <button
          className="text-left text-leland-gray-light underline"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Hide details' : 'More details'}
        </button>
        {isExpanded ? (
          <div className="mt-1 flex flex-col gap-0.5">
            {cohort.coaches.length === 1 ? (
              <div className="flex items-center gap-1 text-leland-gray-light">
                <ProfileImage
                  className="size-4"
                  firstName={cohort.coaches[0].user.firstName}
                  lastName={cohort.coaches[0].user.lastName}
                  pictureLink={cohort.coaches[0].user.pictureLink}
                />
                <div>Hosted by {formatName(cohort.coaches[0].user)}</div>
              </div>
            ) : null}
            <div className="flex items-center gap-1 text-leland-gray-light">
              <IconCalendar className="size-4" />
              <div>{pluralize(numberOfSessions, 'session')}</div>
            </div>
            {cadences.length > 0
              ? cadences.map((cadence) => (
                  <div
                    className="flex items-center gap-1 text-leland-gray-light"
                    key={cadence}
                  >
                    <IconArrowRound className="size-4" />
                    <div>{cadence}</div>
                  </div>
                ))
              : null}
            {hasSameDuration ? (
              <div className="flex items-center gap-1 text-leland-gray-light">
                <IconClock className="size-4" />
                <div>
                  {curriculums[0].duration}-minute{' '}
                  {pluralize(numberOfSessions, 'session', {
                    excludeNumber: true,
                  })}
                </div>
              </div>
            ) : null}
            <div className="flex items-center gap-1 text-leland-gray-light">
              <IconVideo className="size-4" />
              <div>Recorded for your review</div>
            </div>
            <button
              id="bootcamp_page:curriculum_link"
              className="text-left text-leland-primary underline"
              onClick={onViewCurriculum}
            >
              View curriculum
            </button>
          </div>
        ) : null}
        <BootcampUrgencyTag cohortUrn={cohort.id} />
      </div>
      <div>
        <Button
          label="Enroll"
          buttonColor={ButtonColor.PRIMARY}
          onClick={onEnroll}
          disabled={seatsLeft === 0}
        />
      </div>
    </div>
  );
};
